import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { Pressable, StyleSheet, View } from 'react-native';
import { RadioButton } from 'react-native-paper';

import { useInfoModal } from '@/contexts/infoModalContext';
import { i18n } from '@/utils/i18n';

import InfoButton from '../buttons/InfoButton';
import Text from '../Text';
import { OptionType } from './types';

export type RadioButtonGroupProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  options: OptionType[];
  groupLabel?: React.ReactNode;
  showNoneOption?: boolean;
  showError?: boolean;
  labelMargin?: number;
};

const RadioButtonGroup = <T extends FieldValues>({
  name,
  control,
  options,
  groupLabel,
  showNoneOption = true,
  showError = true,
  labelMargin = 18
}: RadioButtonGroupProps<T>) => {
  const styles = useStyle(labelMargin);

  const { showInfo } = useInfoModal();

  const showToolTip = (title: string, message: string) => {
    showInfo({
      type: 'tooltip',
      title: title,
      message: message
    });
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <View>
          {groupLabel && <View style={styles.groupLabel}>{groupLabel}</View>}

          <RadioButton.Group onValueChange={(newValue) => onChange(newValue)} value={value}>
            {showNoneOption && (
              <View style={styles.optionContainer} key={`select-${name}-none`}>
                <RadioButton.Android value="none" color="black" />
                <Text style={styles.labelText}>{i18n.t('mix.none')}</Text>
              </View>
            )}
            {options.map((option, index) => (
              <Pressable
                key={index}
                {...(option.extraInfo && {
                  onPress: () => option.extraInfo && showToolTip(option.label, option.extraInfo)
                })}>
                <View style={styles.optionContainer} key={`select-${name}-${option}-${index}`}>
                  <RadioButton.Android value={option.value} color="black" disabled={option.disabled ?? false} />
                  <Text style={styles.labelText}>{option.label}</Text>
                  {option.extraInfo && <InfoButton title={option.label} message={option.extraInfo} type="modal" />}
                </View>
              </Pressable>
            ))}
          </RadioButton.Group>
          {showError && !!error?.message && <Text style={styles.errorText}>{error.message}</Text>}
        </View>
      )}
    />
  );
};

export default RadioButtonGroup;

const useStyle = (labelMargin: number) => {
  const styles = StyleSheet.create({
    optionContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 2
    },

    groupLabel: {
      marginBottom: labelMargin
    },

    labelText: {
      fontSize: 14,
      fontWeight: '400'
    },

    errorText: {
      marginTop: 10,
      fontSize: 12,
      color: 'red'
    }
  });

  return styles;
};
